/* eslint-disable react/jsx-pascal-case */
import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import {
  Box,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControlLabel,
  Checkbox,
  Typography,
  IconButton,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import {
  MaterialReactTable,
  MRT_GlobalFilterTextField,
  useMaterialReactTable,
} from "material-react-table";
import { useTheme } from "@mui/material/styles";
import { PageHeader } from "../../ui";
import {
  getSecurityDataColumns,
  getMissingConversionRatioColumns,
} from "../../table/columnsConfig/ConvertsAttributeDataColumns";
import {
  getCommonBodyCellProps,
  getCommonHeadCellProps,
} from "../../table/columnsConfig/commonUtils";
import ExportToCSVButton from "../../ExportToCsvButton";
import {
  createConvertAttributesInfo,
  updateConvertAttributesInfo,
  deleteConvertAttributesInfo,
  fetchConvertAttributesInfo,
  fetchConvertsScannerLatestDateData,
} from "../../../services/Axios";

const columnsToExport = ["isin", "name", "conversion_ratio", "exclude_from_scanner", "actions"];
const missingConversionRatioColumnsToExport = ["isin", "name", "addConversionRatio"];

const SecurityDataTab = () => {
  const theme = useTheme();
  const [convertAttributesData, setConvertAttributesData] = useState([]);
  const [convertsScannerData, setConvertsScannerData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [selectedConversionRatio, setSelectedConversionRatio] = useState("");
  const [selectedExcludeFromScanner, setSelectedExcludeFromScanner] = useState(false);
  // Local state for TextField to prevent lag
  const [localConversionRatio, setLocalConversionRatio] = useState("");
  const conversionRatioInputRef = useRef(null);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [deleteConfirmation, setDeleteConfirmation] = useState({
    open: false,
    isin: "",
    attributeId: null,
  });
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [modalIsin, setModalIsin] = useState("");
  const [modalName, setModalName] = useState("");
  const [modalAttributeId, setModalAttributeId] = useState(null);
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  // State for left table (ISINs with data)
  const [isinsWithData, setIsinsWithData] = useState([]);
  const [
    isinsWithDataGlobalFilter,
    setIsinsWithDataGlobalFilter,
  ] = useState("");

  // State for right table (ISINs missing conversion ratio)
  const [isinsMissingConversionRatio, setIsinsMissingConversionRatio] = useState([]);
  const [
    isinsMissingConversionRatioGlobalFilter,
    setIsinsMissingConversionRatioGlobalFilter,
  ] = useState("");

  // Fetch data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const [attributesData, latestDateResponse] = await Promise.all([
          fetchConvertAttributesInfo(),
          fetchConvertsScannerLatestDateData(),
        ]);
        setConvertAttributesData(attributesData || []);
        // Extract data array from the response (response structure: {status, latest_date, data, count})
        const scannerData = latestDateResponse?.data || [];
        setConvertsScannerData(scannerData);
      } catch (err) {
        console.error("Error fetching data:", err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  // Process data to separate ISINs with data vs missing conversion ratio
  useEffect(() => {
    // Left table: All data from GlzConvertAttributesInfo
    if (convertAttributesData?.length > 0) {
      const withData = convertAttributesData.map((item) => ({
        isin: item.isin,
        name: item.name || "N/A",
        conversion_ratio: item.conversion_ratio,
        exclude_from_scanner: item.exclude_from_scanner || false,
        id: item.id || null,
      }));
      setIsinsWithData(withData);
    } else {
      setIsinsWithData([]);
    }

    // Right table: ISINs from latest scanner data that are NOT in GlzConvertAttributesInfo
    // Note: latest-date-data endpoint already returns only the latest date data, so we don't need to filter by date
    if (convertsScannerData?.length > 0) {
      // Get all unique ISINs from the latest data (already filtered by the endpoint)
      const latestIsins = convertsScannerData
        .filter((item) => item.isin)
        .map((item) => item.isin)
        .filter((isin, index, arr) => arr.indexOf(isin) === index); // Remove duplicates

      // Get all ISINs that exist in GlzConvertAttributesInfo
      const isinsInAttributes = new Set(
        (convertAttributesData || []).map((item) => item.isin)
      );

      // Right table: ISINs in latest data but NOT in GlzConvertAttributesInfo
      const missingInAttributes = latestIsins
        .filter((isin) => !isinsInAttributes.has(isin))
        .map((isin) => {
          // Get name from scanner data
          const scannerData = convertsScannerData.find(
            (item) => item.isin === isin
          );
          return {
            isin: isin,
            name: scannerData?.name || "N/A",
          };
        });
      setIsinsMissingConversionRatio(missingInAttributes);
    } else {
      setIsinsMissingConversionRatio([]);
    }
  }, [convertsScannerData, convertAttributesData]);

  // Function to open add modal
  const handleOpenAddModal = useCallback((isin, name) => {
    setModalIsin(isin);
    setModalName(name || "N/A");
    setModalAttributeId(null);
    setSelectedConversionRatio("");
    setLocalConversionRatio("");
    setSelectedExcludeFromScanner(false);
    setAddModalOpen(true);
  }, []);

  // Function to open edit modal
  const handleOpenEditModal = useCallback((isin, currentConversionRatio, currentExcludeFromScanner, attributeId) => {
    const item = isinsWithData.find((item) => item.isin === isin);
    const ratioValue = currentConversionRatio?.toString() || "";
    setModalIsin(isin);
    setModalName(item?.name || "N/A");
    setModalAttributeId(attributeId);
    setSelectedConversionRatio(ratioValue);
    setLocalConversionRatio(ratioValue);
    setSelectedExcludeFromScanner(currentExcludeFromScanner || false);
    setEditModalOpen(true);
  }, [isinsWithData]);

  // Function to close modals
  const handleCloseModals = useCallback(() => {
    setAddModalOpen(false);
    setEditModalOpen(false);
    setModalIsin("");
    setModalName("");
    setModalAttributeId(null);
    setSelectedConversionRatio("");
    setLocalConversionRatio("");
    setSelectedExcludeFromScanner(false);
  }, []);

  // Sync local state to parent state when modal opens/closes
  useEffect(() => {
    if (addModalOpen || editModalOpen) {
      setLocalConversionRatio(selectedConversionRatio);
    }
  }, [addModalOpen, editModalOpen, selectedConversionRatio]);

  // Handle TextField change with local state for performance
  const handleConversionRatioChange = useCallback((e) => {
    const value = e.target.value;
    setLocalConversionRatio(value);
    // Debounce the parent state update to reduce re-renders
    if (conversionRatioInputRef.current) {
      clearTimeout(conversionRatioInputRef.current);
    }
    conversionRatioInputRef.current = setTimeout(() => {
      setSelectedConversionRatio(value);
    }, 100);
  }, []);

  // Sync on blur to ensure value is saved
  const handleConversionRatioBlur = useCallback(() => {
    if (conversionRatioInputRef.current) {
      clearTimeout(conversionRatioInputRef.current);
    }
    setSelectedConversionRatio(localConversionRatio);
  }, [localConversionRatio]);

  // Function to handle adding conversion ratio to an ISIN
  const handleAddConversionRatio = async () => {
    const ratioValue = localConversionRatio.trim();
    if (!ratioValue) {
      setMessage({
        type: "error",
        text: "Please enter a conversion ratio",
      });
      setTimeout(() => setMessage({ type: "", text: "" }), 3000);
      return;
    }

    try {
      // Create the new convert attributes entry
      const newConvertAttribute = {
        isin: modalIsin,
        name: modalName || "",
        conversion_ratio: parseFloat(ratioValue),
        exclude_from_scanner: selectedExcludeFromScanner || false,
      };

      // Call the API to create the convert attribute
      const response = await createConvertAttributesInfo(newConvertAttribute);

      // Show success message
      setMessage({
        type: "success",
        text: `Conversion ratio added for ISIN "${modalIsin}" successfully!`,
      });

      // Refresh data from server
      const updatedData = await fetchConvertAttributesInfo();
      setConvertAttributesData(updatedData || []);

      // Close modal
      handleCloseModals();

      // Clear message after 3 seconds
      setTimeout(() => setMessage({ type: "", text: "" }), 3000);
    } catch (error) {
      console.error("Error adding conversion ratio:", error);
      setMessage({
        type: "error",
        text: `Failed to add conversion ratio: ${error.message}`,
      });
      setTimeout(() => setMessage({ type: "", text: "" }), 5000);
    }
  };

  // Function to start editing an ISIN (opens modal)
  const handleStartEdit = useCallback((isin, name) => {
    // If name is provided, use it; otherwise find it from the data
    if (name) {
      handleOpenAddModal(isin, name);
    } else {
      const item = isinsMissingConversionRatio.find((item) => item.isin === isin);
      if (item) {
        handleOpenAddModal(isin, item.name);
      }
    }
  }, [isinsMissingConversionRatio, handleOpenAddModal]);

  // Function to start editing an ISIN with data (opens modal)
  const handleStartEditWithData = useCallback((isin, currentConversionRatio, currentExcludeFromScanner, attributeId) => {
    const item = isinsWithData.find((item) => item.isin === isin);
    if (item) {
      handleOpenEditModal(isin, currentConversionRatio, currentExcludeFromScanner, attributeId || item.id);
    }
  }, [isinsWithData, handleOpenEditModal]);

  // Function to handle delete confirmation
  const handleDeleteClick = useCallback((isin, attributeId) => {
    setDeleteConfirmation({ open: true, isin, attributeId });
  }, []);

  // Function to handle delete confirmation
  const handleDeleteConfirm = async () => {
    try {
      const { isin, attributeId } = deleteConfirmation;

      // Call the API to delete the convert attribute
      await deleteConvertAttributesInfo(attributeId);

      // Show success message
      setMessage({
        type: "success",
        text: `ISIN "${isin}" deleted successfully!`,
      });

      // Remove from local state
      setIsinsWithData((prev) =>
        prev.filter((item) => item.isin !== isin)
      );

      // Refresh data from server
      const updatedData = await fetchConvertAttributesInfo();
      setConvertAttributesData(updatedData || []);

      // Close confirmation dialog
      setDeleteConfirmation({
        open: false,
        isin: "",
        attributeId: null,
      });

      // Clear message after 3 seconds
      setTimeout(() => setMessage({ type: "", text: "" }), 3000);
    } catch (error) {
      console.error("Error deleting convert attribute:", error);
      setMessage({
        type: "error",
        text: `Failed to delete ISIN: ${error.message}`,
      });
      setTimeout(() => setMessage({ type: "", text: "" }), 5000);
    }
  };

  // Function to update conversion ratio for existing ISIN
  const handleUpdateAttributes = async () => {
    const ratioValue = localConversionRatio.trim();
    if (!ratioValue) {
      setMessage({
        type: "error",
        text: "Please enter a conversion ratio",
      });
      setTimeout(() => setMessage({ type: "", text: "" }), 3000);
      return;
    }

    try {
      if (!modalAttributeId) {
        throw new Error("Attribute ID not found");
      }

      // Update the convert attribute entry
      const updatedAttribute = {
        isin: modalIsin,
        name: modalName || "",
        conversion_ratio: parseFloat(ratioValue),
        exclude_from_scanner: selectedExcludeFromScanner || false,
      };

      // Call the API to update the convert attribute
      await updateConvertAttributesInfo(modalAttributeId, updatedAttribute);

      // Show success message
      setMessage({
        type: "success",
        text: `Conversion ratio updated for ISIN "${modalIsin}" successfully!`,
      });

      // Refresh data from server
      const updatedData = await fetchConvertAttributesInfo();
      setConvertAttributesData(updatedData || []);

      // Close modal
      handleCloseModals();

      // Clear message after 3 seconds
      setTimeout(() => setMessage({ type: "", text: "" }), 3000);
    } catch (error) {
      console.error("Error updating conversion ratio:", error);
      setMessage({
        type: "error",
        text: `Failed to update conversion ratio: ${error.message}`,
      });
      setTimeout(() => setMessage({ type: "", text: "" }), 5000);
    }
  };

  // Memoize columns to prevent recreation on every render
  const securityDataColumns = useMemo(
    () => getSecurityDataColumns(theme, {
      onStartEdit: handleStartEditWithData,
      onDeleteClick: handleDeleteClick,
    }),
    [theme, handleStartEditWithData, handleDeleteClick]
  );

  const missingConversionRatioColumns = useMemo(
    () => getMissingConversionRatioColumns(theme, {
      onStartEdit: handleStartEdit,
    }),
    [theme, handleStartEdit]
  );

  // Left table configuration
  const isinsWithDataTable = useMaterialReactTable({
    columns: securityDataColumns,
    data: isinsWithData,
    enableColumnDragging: false,
    enableGrouping: false,
    enableSorting: true,
    enablePagination: false,
    enableStickyHeader: true,
    enableStickyFooter: true,
    enableColumnResizing: true,
    enableBottomToolbar: false,
    enablePinning: true,
    columnResizeMode: "onChange",
    enableTopToolbar: false,
    enableColumnActions: false,
    enableGlobalFilter: true,
    globalFilterFn: "contains",
    initialState: {
      sorting: [{ id: "isin", desc: false }],
      density: "compact",
      showColumnFilters: false,
      showGlobalFilter: true,
      globalFilter: isinsWithDataGlobalFilter,
    },
    state: {
      globalFilter: isinsWithDataGlobalFilter,
    },
    onGlobalFilterChange: setIsinsWithDataGlobalFilter,
    muiTableHeadCellProps: {
      ...getCommonHeadCellProps(theme),
    },
    muiTableBodyCellProps: {
      ...getCommonBodyCellProps(theme),
    },
    muiTableBodyRowProps: {
      sx: {
        backgroundColor: theme.palette.background.paper,
        padding: "0px",
        height: {
          xs: "30px",
          sm: "25px",
        },
        transition: "background-color 0.2s ease",
        "&:hover": {
          backgroundColor: theme.palette.grey[400],
        },
        "&:nth-of-type(odd)": {
          backgroundColor: theme.palette.action.hover,
        },
      },
    },
    muiTableContainerProps: {
      sx: {
        height: {
          xs: "400px",
          sm: "500px",
          md: "calc(100vh - 250px)",
        },
        marginBottom: "0px",
        width: "100%",
        maxWidth: "100%",
        overflowX: "auto",
        overflowY: "auto",
        position: "relative",
        borderRadius: 0,
        boxShadow: theme.shadows[1],
        "&::-webkit-scrollbar": {
          height: "8px",
          width: "8px",
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: theme.palette.grey[100],
          borderRadius: 0,
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: theme.palette.grey[300],
          borderRadius: 0,
          "&:hover": {
            backgroundColor: theme.palette.grey[400],
          },
        },
      },
    },
    muiTableProps: {
      sx: {
        width: "100%",
        tableLayout: "auto",
        "& .MuiTableHead-root": {
          borderBottom: `2px solid ${theme.palette.divider}`,
        },
        "& .MuiTableHead-root .MuiTableRow-root": {
          height: "auto",
          minHeight: {
            xs: "30px",
            sm: "35px",
          },
        },
        "& .MuiTableHead-root .MuiTableCell-root": {
          padding: {
            xs: theme.spacing(0.5, 0.25, 0.25, 0.25),
            sm: theme.spacing(0.75, 0.5, 0.25, 0.5),
          },
          "& .Mui-TableHeadCell-Content": {
            width: "100%",
            maxWidth: "100%",
            whiteSpace: "normal !important",
            textOverflow: "unset",
            display: "flex",
            alignItems: "center",
          },
          "& .Mui-TableHeadCell-Content-Labels": {
            width: "100%",
            whiteSpace: "normal !important",
            textOverflow: "unset",
          },
          "& .Mui-TableHeadCell-Content-Wrapper": {
            whiteSpace: "normal !important",
            textOverflow: "unset",
            overflow: "visible",
          },
        },
        "& .MuiTableFooter-root": {
          borderTop: `2px solid ${theme.palette.divider}`,
          position: "sticky",
          bottom: 0,
          zIndex: 100,
          backgroundColor: theme.palette.primary.dark,
        },
        "& .MuiTableFooter-root .MuiTableRow-root": {
          height: "40px",
        },
        "& .MuiTableFooter-root .MuiTableCell-root": {
          padding: {
            xs: theme.spacing(1, 0.5),
            sm: theme.spacing(1.5, 1),
          },
          lineHeight: "1.2",
          backgroundColor: theme.palette.primary.dark,
          fontSize: {
            xs: "0.75rem",
            sm: "0.875rem",
          },
          color: theme.palette.primary.contrastText,
        },
      },
    },
  });

  // Right table configuration
  const isinsMissingConversionRatioTable = useMaterialReactTable({
    columns: missingConversionRatioColumns,
    data: isinsMissingConversionRatio,
    enableColumnDragging: false,
    enableGrouping: false,
    enableSorting: true,
    enablePagination: false,
    enableStickyHeader: true,
    enableStickyFooter: true,
    enableColumnResizing: true,
    enableBottomToolbar: false,
    enablePinning: true,
    columnResizeMode: "onChange",
    enableTopToolbar: false,
    enableColumnActions: false,
    enableGlobalFilter: true,
    globalFilterFn: "contains",
    initialState: {
      sorting: [{ id: "isin", desc: false }],
      density: "compact",
      showColumnFilters: false,
      showGlobalFilter: true,
      globalFilter: isinsMissingConversionRatioGlobalFilter,
    },
    state: {
      globalFilter: isinsMissingConversionRatioGlobalFilter,
    },
    onGlobalFilterChange: setIsinsMissingConversionRatioGlobalFilter,
    muiTableHeadCellProps: {
      ...getCommonHeadCellProps(theme),
    },
    muiTableBodyCellProps: {
      ...getCommonBodyCellProps(theme),
    },
    muiTableBodyRowProps: {
      sx: {
        backgroundColor: theme.palette.background.paper,
        padding: "0px",
        height: {
          xs: "30px",
          sm: "25px",
        },
        transition: "background-color 0.2s ease",
        "&:hover": {
          backgroundColor: theme.palette.grey[400],
        },
        "&:nth-of-type(odd)": {
          backgroundColor: theme.palette.action.hover,
        },
      },
    },
    muiTableContainerProps: {
      sx: {
        height: {
          xs: "400px",
          sm: "500px",
          md: "calc(100vh - 250px)",
        },
        marginBottom: "0px",
        width: "100%",
        maxWidth: "100%",
        overflowX: "auto",
        overflowY: "auto",
        position: "relative",
        borderRadius: 0,
        boxShadow: theme.shadows[1],
        "&::-webkit-scrollbar": {
          height: "8px",
          width: "8px",
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: theme.palette.grey[100],
          borderRadius: 0,
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: theme.palette.grey[300],
          borderRadius: 0,
          "&:hover": {
            backgroundColor: theme.palette.grey[400],
          },
        },
      },
    },
    muiTableProps: {
      sx: {
        width: "100%",
        tableLayout: "auto",
        "& .MuiTableHead-root": {
          borderBottom: `2px solid ${theme.palette.divider}`,
        },
        "& .MuiTableHead-root .MuiTableRow-root": {
          height: "auto",
          minHeight: {
            xs: "30px",
            sm: "35px",
          },
        },
        "& .MuiTableHead-root .MuiTableCell-root": {
          padding: {
            xs: theme.spacing(0.5, 0.25, 0.25, 0.25),
            sm: theme.spacing(0.75, 0.5, 0.25, 0.5),
          },
          "& .Mui-TableHeadCell-Content": {
            width: "100%",
            maxWidth: "100%",
            whiteSpace: "normal !important",
            textOverflow: "unset",
            display: "flex",
            alignItems: "center",
          },
          "& .Mui-TableHeadCell-Content-Labels": {
            width: "100%",
            whiteSpace: "normal !important",
            textOverflow: "unset",
          },
          "& .Mui-TableHeadCell-Content-Wrapper": {
            whiteSpace: "normal !important",
            textOverflow: "unset",
            overflow: "visible",
          },
        },
        "& .MuiTableFooter-root": {
          borderTop: `2px solid ${theme.palette.divider}`,
          position: "sticky",
          bottom: 0,
          zIndex: 100,
          backgroundColor: theme.palette.primary.dark,
        },
        "& .MuiTableFooter-root .MuiTableRow-root": {
          height: "40px",
        },
        "& .MuiTableFooter-root .MuiTableCell-root": {
          padding: {
            xs: theme.spacing(1, 0.5),
            sm: theme.spacing(1.5, 1),
          },
          lineHeight: "1.2",
          backgroundColor: theme.palette.primary.dark,
          fontSize: {
            xs: "0.75rem",
            sm: "0.875rem",
          },
          color: theme.palette.primary.contrastText,
        },
      },
    },
  });

  // Show loading state
  if (isLoading) {
    return (
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <div>Loading security data...</div>
      </Box>
    );
  }

  // Show error state
  if (error) {
    return (
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <div>Error loading data: {error}</div>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      {/* Message Display */}
      {message.text && (
        <Box
          sx={{
            mb: 1,
            p: 1.5,
            backgroundColor:
              message.type === "success"
                ? theme.palette.success.light
                : theme.palette.error.light,
            color:
              message.type === "success"
                ? theme.palette.success.contrastText
                : theme.palette.error.contrastText,
            borderRadius: 1,
            border: `1px solid ${
              message.type === "success"
                ? theme.palette.success.main
                : theme.palette.error.main
            }`,
          }}
        >
          {message.text}
        </Box>
      )}

      <Box
        sx={{
          width: "100%",
          overflow: "hidden",
          display: "flex",
          gap: { xs: 1, sm: 2, md: 3 },
          padding: { xs: 0.5, sm: 1 },
          height: "calc(100vh - 120px)",
          flexDirection: { xs: "column", md: "row" },
        }}
      >
        {/* Left Section - ISINs with Conversion Ratio */}
        <Box
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            backgroundColor: theme.palette.background.paper,
            borderRadius: 0,
            boxShadow: theme.shadows[1],
          }}
        >
          <PageHeader
            sx={{
              px: "16px",
              py: "12px",
              justifyContent: "flex-start",
              alignItems: "end",
            }}
            title={"Convert Conversion Ratio Info"}
            action={
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  flexWrap: "wrap",
                  gap: 2,
                }}
              >
                {/* Search Field */}
                <Box sx={{ maxWidth: "280px", flexShrink: 0 }}>
                  <MRT_GlobalFilterTextField
                    table={isinsWithDataTable}
                    sx={{
                      width: "100%",
                      "& .MuiInputBase-root": {
                        backgroundColor: theme.palette.background.paper,
                        borderRadius: theme.shape.borderRadius,
                        border: `1px solid ${theme.palette.divider}`,
                        fontSize: "0.875rem",
                        "&:hover": {
                          backgroundColor: theme.palette.background.paper,
                          borderColor: theme.palette.primary.dark,
                        },
                        "&.Mui-focused": {
                          backgroundColor: theme.palette.background.paper,
                          borderColor: theme.palette.primary.dark,
                          boxShadow: `0 0 0 2px ${theme.palette.primary.dark}20`,
                        },
                      },
                      "& .MuiInputBase-input": {
                        padding: theme.spacing(0.75, 1.25),
                        color: theme.palette.text.primary,
                        "&::placeholder": {
                          color: theme.palette.text.secondary,
                          opacity: 1,
                        },
                      },
                    }}
                  />
                </Box>
                <ExportToCSVButton
                  columns={columnsToExport}
                  theme={theme}
                  globalFilter=""
                  formatCurrency={() => {}}
                  formatNumber={() => {}}
                  data={isinsWithData.map((item) => ({
                    ...item,
                    actions: "Edit/Delete Buttons",
                  }))}
                  filename={"Convert Conversion Ratio Info"}
                />
              </Box>
            }
          />
          <Box
            style={{
              flex: 1,
              overflow: "hidden",
              border: "1px solid #ddd",
              borderRadius: "4px",
            }}
          >
            <MaterialReactTable table={isinsWithDataTable} />
          </Box>
        </Box>

        {/* Right Section - ISINs Missing Conversion Ratio */}
        <Box
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            overflowY: "auto",
            overflowX: "auto",
            backgroundColor: theme.palette.background.paper,
            borderRadius: 0,
            boxShadow: theme.shadows[1],
          }}
        >
          <PageHeader
            sx={{
              px: "16px",
              py: "12px",
              justifyContent: "flex-start",
              alignItems: "end",
            }}
            title={"ISINs Missing in Conversion Ratio"}
            action={
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  flexWrap: "wrap",
                  gap: 2,
                }}
              >
                {/* Search Field */}
                <Box sx={{ maxWidth: "280px", flexShrink: 0 }}>
                  <MRT_GlobalFilterTextField
                    table={isinsMissingConversionRatioTable}
                    sx={{
                      width: "100%",
                      "& .MuiInputBase-root": {
                        backgroundColor: theme.palette.background.paper,
                        borderRadius: theme.shape.borderRadius,
                        border: `1px solid ${theme.palette.divider}`,
                        fontSize: "0.875rem",
                        "&:hover": {
                          backgroundColor: theme.palette.background.paper,
                          borderColor: theme.palette.primary.dark,
                        },
                        "&.Mui-focused": {
                          backgroundColor: theme.palette.background.paper,
                          borderColor: theme.palette.primary.dark,
                          boxShadow: `0 0 0 2px ${theme.palette.primary.dark}20`,
                        },
                      },
                      "& .MuiInputBase-input": {
                        padding: theme.spacing(0.75, 1.25),
                        color: theme.palette.text.primary,
                        "&::placeholder": {
                          color: theme.palette.text.secondary,
                          opacity: 1,
                        },
                      },
                    }}
                  />
                </Box>
                <ExportToCSVButton
                  columns={missingConversionRatioColumnsToExport}
                  theme={theme}
                  globalFilter=""
                  formatCurrency={() => {}}
                  formatNumber={() => {}}
                  data={isinsMissingConversionRatio.map((item) => ({
                    ...item,
                    addConversionRatio: "Add Conversion Ratio Button",
                  }))}
                  filename={"ISINs Missing in Attributes"}
                />
              </Box>
            }
          />
          <Box
            style={{
              flex: 1,
              overflow: "hidden",
              border: "1px solid #ddd",
              borderRadius: "4px",
            }}
          >
            <MaterialReactTable table={isinsMissingConversionRatioTable} />
          </Box>
        </Box>
      </Box>

      {/* Add Conversion Ratio Modal */}
      <Dialog
        open={addModalOpen}
        onClose={handleCloseModals}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: theme.shadows[24],
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 1,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography variant="h6" component="div">
            Add Conversion Ratio
          </Typography>
          <IconButton
            onClick={handleCloseModals}
            size="small"
            sx={{
              color: theme.palette.text.secondary,
              "&:hover": {
                backgroundColor: theme.palette.action.hover,
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                ISIN
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {modalIsin}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                Name
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {modalName}
              </Typography>
            </Box>
            <TextField
              label="Conversion Ratio"
              type="number"
              value={localConversionRatio}
              onChange={handleConversionRatioChange}
              onBlur={handleConversionRatioBlur}
              fullWidth
              required
              inputProps={{ step: "0.0001" }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: theme.palette.divider,
                  },
                  "&:hover fieldset": {
                    borderColor: theme.palette.primary.main,
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: theme.palette.primary.main,
                  },
                },
              }}
              autoFocus
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedExcludeFromScanner}
                  onChange={(e) => setSelectedExcludeFromScanner(e.target.checked)}
                />
              }
              label="Exclude From Scanner"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, pt: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
          <Button onClick={handleCloseModals} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleAddConversionRatio}
            variant="contained"
            disabled={!localConversionRatio || !localConversionRatio.trim()}
            sx={{
              backgroundColor: theme.palette.primary.main,
              "&:hover": {
                backgroundColor: theme.palette.primary.dark,
              },
            }}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Conversion Ratio Modal */}
      <Dialog
        open={editModalOpen}
        onClose={handleCloseModals}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: theme.shadows[24],
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 1,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography variant="h6" component="div">
            Edit Conversion Ratio
          </Typography>
          <IconButton
            onClick={handleCloseModals}
            size="small"
            sx={{
              color: theme.palette.text.secondary,
              "&:hover": {
                backgroundColor: theme.palette.action.hover,
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                ISIN
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {modalIsin}
              </Typography>
            </Box>
            <TextField
              label="Conversion Ratio"
              type="number"
              value={localConversionRatio}
              onChange={handleConversionRatioChange}
              onBlur={handleConversionRatioBlur}
              fullWidth
              required
              inputProps={{ step: "0.0001" }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: theme.palette.divider,
                  },
                  "&:hover fieldset": {
                    borderColor: theme.palette.primary.main,
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: theme.palette.primary.main,
                  },
                },
              }}
              autoFocus
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedExcludeFromScanner}
                  onChange={(e) => setSelectedExcludeFromScanner(e.target.checked)}
                />
              }
              label="Exclude From Scanner"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, pt: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
          <Button onClick={handleCloseModals} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleUpdateAttributes}
            variant="contained"
            disabled={!localConversionRatio || !localConversionRatio.trim()}
            sx={{
              backgroundColor: theme.palette.primary.main,
              "&:hover": {
                backgroundColor: theme.palette.primary.dark,
              },
            }}
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      {deleteConfirmation.open && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
        >
          <Box
            sx={{
              backgroundColor: theme.palette.background.paper,
              borderRadius: 2,
              padding: 3,
              maxWidth: "400px",
              width: "90%",
              boxShadow: theme.shadows[24],
            }}
          >
            <Box
              sx={{
                fontSize: "1.25rem",
                fontWeight: 600,
                color: theme.palette.text.primary,
                mb: 2,
              }}
            >
              Confirm Delete
            </Box>
            <Box
              sx={{
                fontSize: "1rem",
                color: theme.palette.text.secondary,
                mb: 3,
              }}
            >
              Are you sure you want to delete the conversion ratio for ISIN{" "}
              <strong>"{deleteConfirmation.isin}"</strong>? This action cannot be
              undone.
            </Box>
            <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
              <button
                onClick={() =>
                  setDeleteConfirmation({
                    open: false,
                    isin: "",
                    attributeId: null,
                  })
                }
                style={{
                  padding: "8px 16px",
                  fontSize: "14px",
                  backgroundColor: theme.palette.grey[500],
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                style={{
                  padding: "8px 16px",
                  fontSize: "14px",
                  backgroundColor: theme.palette.error.main,
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                Delete
              </button>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default SecurityDataTab;
